import React, { useEffect, useMemo, useState } from 'react';
import ModuleList from '../../components/BtAdminPanel/ModuleList';
import UpdateOrganizationAdminModule from '../../utils/Module/UpdateModule';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { getAllModules, updateModule } from '../../store/slice/moduleSlice';
import { getLoadingState } from '../../utils/Helper/helper';
import { useLocation } from 'react-router-dom';
import IModuleList from '../../utils/ModuleList/ModuleList.interface';
import IModule from '../../utils/Module/Module.interface';
import { getAllRepoOwners, updateRepoOwnerCount } from '../../store/slice/repoOwnerSlice';

const Modules: React.FC = () => {
  const dispatch = useAppDispatch();
  const moduleState = useAppSelector((state) => state.modules);
  const repoOwnerState = useAppSelector((state) => state.repoOwners);
  const modules = moduleState.data ?? [];
  const repoOwners = repoOwnerState.data ?? [];
  const isLoading = getLoadingState(moduleState);
  const [visibility, setVisibility] = useState<boolean>(false);
  const [queryModule, setQueryModule] = useState<string>('');
  const [currentPageModule, setCurrentPageModule] = useState<number>(1);
  const [openPopupIndex, setOpenPopupIndex] = useState<number | null>(null);
  const [responseMessage, setResponseMessage] = useState<string>('Please Wait...');
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [selectedModules, setSelectedModules] = useState<Array<{ moduleName: string; isSystemModule: boolean; repoOwnerName?: string }>>([]);
  const [pendingOwnerChanges, setPendingOwnerChanges] = useState<Record<string, { newOwner: string; previousOwner: string }>>({});
  const location = useLocation();
  const highlightModule = (location.state as { highlightModule?: string })?.highlightModule;
  const screenHeight = window.innerHeight;
  const modulesPerPage = screenHeight ? Math.ceil((screenHeight - 210) / 90) : 10;
  const sortedDataModule = useMemo(() => [...modules].sort((a, b) => a.moduleName.localeCompare(b.moduleName)), [modules]);
  const filteredDataModule = useMemo(() => sortedDataModule.filter((user: { moduleName: string }) =>
    user.moduleName.toLowerCase().includes(queryModule.toLowerCase())
  ), [sortedDataModule, queryModule]);
  // Calculate the index of the first and last items to display on the current page
  const indexOfLastItemModule = currentPageModule * modulesPerPage;
  const indexOfFirstItemModule = indexOfLastItemModule - modulesPerPage;
  const currentData = filteredDataModule.slice(indexOfFirstItemModule, indexOfLastItemModule);

  const resetPageAndSearchModule = (newQuery: string) => {
    setQueryModule(newQuery);
    handlePageChangeModule(1);
  };

  const popupCloseAlertHandler = (e: boolean) => {
    setShowAlert(e);
    setResponseMessage('Please Wait...');
  };

  const handlePageChangeModule = (pageNumber: number) => {
    setCurrentPageModule(pageNumber);
  };

  const handleSubmit = async () => {
    setShowAlert(true);
    const userObj = new UpdateOrganizationAdminModule(selectedModules[0] as IModule);
    const updateStatus = await userObj.updateModule(selectedModules as IModule[]);

    const hasOwnerChanges = selectedModules.some((m) => m.repoOwnerName !== undefined);
    const hasCheckboxChanges = selectedModules.some((m) => m.repoOwnerName === undefined);
    const errorMessage = 'Failed to update module';
    const successMessage = hasOwnerChanges && !hasCheckboxChanges
      ? 'Owner assigned successfully!'
      : hasOwnerChanges && hasCheckboxChanges
        ? 'Modules and Owner updated successfully!!'
        : 'Modules updated successfully!!';
    const isSuccess = updateStatus.statusCode === 200 || updateStatus.statusCode === 201;
    const responseBody = isSuccess ? successMessage : errorMessage;

    if (isSuccess) {
      selectedModules.forEach((module) => {
        const existingModule = modules.find((m: IModuleList) => m.moduleName === module.moduleName);
        if (existingModule) {
          const updatedModuleData: IModuleList = {
            ...existingModule,
            isSystemModule: module.repoOwnerName !== undefined ? existingModule.isSystemModule : !existingModule.isSystemModule,
            owner: module.repoOwnerName !== undefined ? module.repoOwnerName : existingModule.owner
          };
          dispatch(updateModule(updatedModuleData));
        }
      });

      // Update repo owner counts
      Object.entries(pendingOwnerChanges).forEach(([, { newOwner, previousOwner }]) => {
        if (previousOwner) dispatch(updateRepoOwnerCount({ ownerName: previousOwner, delta: -1 }));
        if (newOwner) dispatch(updateRepoOwnerCount({ ownerName: newOwner, delta: 1 }));
      });
    }

    setSelectedModules([]);
    setPendingOwnerChanges({});
    setResponseMessage(responseBody);
    setSubmitted(true);
    void dispatch(getAllModules());
  };

  const handleCheckboxChange = async (moduleName: string, isSystemModule: boolean) => {
    // Check if the module is already selected
    const isModuleSelectedIndex = selectedModules.findIndex(
      (module: { moduleName: string }) => module.moduleName === moduleName
    );

    // Update the selected modules array
    if (isModuleSelectedIndex !== -1) {
      // Module is already selected, so remove it
      const updatedModules = [...selectedModules];
      updatedModules.splice(isModuleSelectedIndex, 1);
      setSelectedModules(updatedModules);
    } else {
      // Module is not selected, so add it
      const updatedModules = [...selectedModules, { moduleName, isSystemModule }];
      setSelectedModules(updatedModules);
    }

    setSubmitted(false);
  };

  // Close the pop-up form
  const popupCloseHandler = () => {
    setVisibility(false);
    setOpenPopupIndex(null);
  };
  // Open the popup for the specified row
  const popupOpenHandler = (index: number) => {
    setVisibility(true);
    setOpenPopupIndex(index);
  };

  useEffect(() => {
    resetPageAndSearchModule('');
    if (moduleState.status !== 'loading') {
      void dispatch(getAllModules());
    }
    if (!repoOwnerState.data) {
      void dispatch(getAllRepoOwners());
    }
  }, []);

  useEffect(() => {
    if (highlightModule && modules.length) {
      const sorted = [...modules].sort((a, b) => a.moduleName.localeCompare(b.moduleName));
      const index = sorted.findIndex((mod: IModuleList) => mod.moduleName === highlightModule);
      if (index !== -1) {
        const page = Math.floor(index / modulesPerPage) + 1;
        setCurrentPageModule(page);
      }
    }
  }, [highlightModule, modules, modulesPerPage]);

  const handleOwnerChange = (moduleName: string, ownerName: string) => {
    const existingModule = modules.find((m: IModuleList) => m.moduleName === moduleName);
    if (!existingModule) return;

    const previousOwner = pendingOwnerChanges[moduleName]?.previousOwner ?? (existingModule.owner || '');

    // If reverting to original owner, remove from selections
    if (ownerName === (existingModule.owner || '')) {
      setSelectedModules((prev) => prev.filter((m) => m.moduleName !== moduleName || m.repoOwnerName === undefined));
      setPendingOwnerChanges((prev) => {
        const { [moduleName]: _, ...rest } = prev;
        return rest;
      });
    } else {
      const existingIndex = selectedModules.findIndex((m) => m.moduleName === moduleName && m.repoOwnerName !== undefined);
      const entry = { moduleName, isSystemModule: existingModule.isSystemModule, repoOwnerName: ownerName };
      if (existingIndex !== -1) {
        const updated = [...selectedModules];
        updated[existingIndex] = entry;
        setSelectedModules(updated);
      } else {
        setSelectedModules((prev) => [...prev, entry]);
      }
      setPendingOwnerChanges((prev) => ({ ...prev, [moduleName]: { newOwner: ownerName, previousOwner } }));
    }
    setSubmitted(false);
  };

  return (
    <ModuleList
      isLoading={isLoading}
      visibility={visibility}
      popupOpenHandler={popupOpenHandler}
      popupCloseHandler={popupCloseHandler}
      openPopupIndex={openPopupIndex}
      itemsPerPage={modulesPerPage}
      currentPage={currentPageModule}
      currentData={currentData}
      handlePageChange={handlePageChangeModule}
      responseMessage={responseMessage}
      showAlert={showAlert}
      popupCloseAlertHandler={popupCloseAlertHandler}
      submitted={submitted}
      selectedModules={selectedModules}
      handleSubmit={handleSubmit}
      handleCheckboxChange={handleCheckboxChange}
      resetPageAndSearch={resetPageAndSearchModule}
      filteredDataModule={filteredDataModule}
      highlightedModuleName={highlightModule}
      showPagination={true}
      repoOwners={repoOwners}
      onOwnerChange={handleOwnerChange}
      pendingOwnerChanges={pendingOwnerChanges}
    />
  );
};

export default Modules;
